/* eslint-disable no-param-reassign */
import { useMemo } from 'react';
import { DateTime, Interval } from 'luxon';
import { cast, types } from 'mobx-state-tree';
import type {
  Instance,
  SnapshotIn,
  SnapshotOut,
} from 'mobx-state-tree/dist/core/type/type';

import { GoogleCalendarEventModel, SlotModel } from '@/models/calendar';
import { initializeStore } from '@/stores/initializer';
import { DateHelpers } from '@/utils/dateTime';
import type {
  GoogleCalendarEventModelType,
  SlotModelType,
} from '@/models/calendar';
import type { WeekBeginEnd } from '@/types/common';

export const MentorCalendarStore = types
  .model({
    // duplicate tz here to simplify dates update on tz change
    tz: types.maybe(types.string),
    startDate: types.maybe(types.string),
    endDate: types.maybe(types.string),
    googleCalendarEnabled: types.maybe(types.boolean),
    canManageSlots: types.maybe(types.boolean),
    slots: types.array(SlotModel),
    googleCalendarEvents: types.array(GoogleCalendarEventModel),
    selectedWeekIndex: types.maybe(types.number),
  })
  .views((self) => ({
    get isLoaded(): boolean {
      return !!self.startDate;
    },
    get bookedSlots(): SlotModelType[] {
      return self.slots.filter((s) => !!s.interview);
    },
    get weeks(): WeekBeginEnd[] {
      if (self.startDate && self.endDate && self.tz) {
        return DateHelpers.convertDateRangeToWeeks(
          self.startDate,
          self.endDate,
          self.tz,
        );
      }

      return [];
    },
  }))
  .views((self) => ({
    get activeWeek(): WeekBeginEnd | null {
      if (self.selectedWeekIndex === undefined) {
        return null;
      }

      return self.weeks[self.selectedWeekIndex];
    },
  }))
  .views((self) => ({
    isInCurrentWeek(startIso: string) {
      if (!self.activeWeek) {
        return false;
      }

      return Interval.fromDateTimes(
        self.activeWeek.begin,
        self.activeWeek.end,
      ).contains(DateTime.fromISO(startIso));
    },
  }))
  .views((self) => ({
    /**
     * Checks if the given slot is in currently selected week.
     * E.g.
     *  week 23.09 - 29.09, slot 25.09 → true
     *  week 23.09 - 29.09, slot 01.10 → false
     */
    isSlotInCurrentWeek(slot: SlotModelType): boolean {
      return self.isInCurrentWeek(slot.start);
    },
  }))
  .actions((self) => ({
    setTz(tz: string) {
      self.tz = tz;
    },
    setSelectedWeekIndex(index: number) {
      self.selectedWeekIndex = index;
    },
    init(
      startIso: string,
      endIso: string,
      googleCalendarEnabled: boolean,
      canManageSlots: boolean,
      tz: string,
    ) {
      self.startDate = startIso;
      self.endDate = endIso;
      self.googleCalendarEnabled = googleCalendarEnabled;
      self.canManageSlots = canManageSlots;
      self.tz = tz;
      self.selectedWeekIndex = 0;
    },
    addSlot(slot: SlotModelType) {
      self.slots = cast([...self.slots, slot]);
    },
    setSlots(slots: SlotModelType[]) {
      self.slots = cast(slots);
    },
    deleteSlot(slot: SlotModelType) {
      self.slots = cast(self.slots.filter((s) => s.pk !== slot.pk));
    },
    setGoogleCalendarEvents(events: GoogleCalendarEventModelType[]) {
      self.googleCalendarEvents = cast(events);
    },
  }))
  .named('MentorCalendarStore');

export const useMentorCalendarStore = (
  initialState: MentorCalendarStoreSnapshotIn | null = null,
): MentorCalendarStoreType | null => {
  return useMemo(
    () => initializeStore(MentorCalendarStore, initialState),
    [initialState],
  );
};

export type MentorCalendarStoreType = Instance<typeof MentorCalendarStore> & {};

export type MentorCalendarStoreSnapshotIn = SnapshotIn<
  typeof MentorCalendarStore
> & {};

export type MentorCalendarStoreSnapshotOut = SnapshotOut<
  typeof MentorCalendarStore
> & {};
